import React, { useContext, useMemo, useState } from 'react'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import AttachmentIcon from '@mui/icons-material/Attachment'
import API from '../../../../../services/API'
import BigModal from '../../../../../components/Modals/BigModal'
import CustomDataGrid from '../../../../../components/DataGrid/CustomDataGrid'
import { GlobalContext } from '../../../../../App'
import { VehicleContext } from '../../SingleVehicle'
import { InfoList, SectionHeader, formatDate } from '../SectionKit'
import { serviceModalStyle } from '../ServicingMotInsurance/AddService'
import AddLolerInspection from './AddLolerInspection'
import { LOLER_STATE, OUTCOME_COLOR } from './lolerOptions'

const equipmentName = (record) =>
  [record?.equipmentType, record?.serialNumber && `#${record.serialNumber}`]
    .filter(Boolean)
    .join(' ')

const StateChip = ({ state }) => {
  const meta = LOLER_STATE[state]
  if (!meta) return null
  return (
    <Chip
      size="small"
      label={meta.short}
      color={meta.tone}
      variant={state === 'ok' ? 'outlined' : 'filled'}
      sx={{ borderRadius: 1, fontWeight: 700 }}
    />
  )
}

const dueText = (item) => {
  if (!item.nextExaminationDue) return '—'
  const due = moment(item.nextExaminationDue)
  const days = due.startOf('day').diff(moment().startOf('day'), 'days')
  const when =
    days < 0 ? `${-days} day${days === -1 ? '' : 's'} overdue` : days === 0 ? 'today' : `in ${days} day${days === 1 ? '' : 's'}`
  return `${formatDate(item.nextExaminationDue)} (${when})`
}

// LOLER thorough examinations of the vehicle's lifting equipment, at the top
// of the Safety Checks tab. Status comes from the server `lolerStatus`
// virtual so it matches the hero tile and the fleet list.
const LolerSection = () => {
  const { vehicle, setVehicle } = useContext(VehicleContext)
  const { setToast } = useContext(GlobalContext)
  // { existing } to edit a record, { prefill } to re-examine equipment, {} new
  const [form, setForm] = useState(null)
  const [busy, setBusy] = useState(null)

  const inspections = vehicle?.lolerInspections ?? []
  const status = vehicle?.lolerStatus
  const byId = useMemo(
    () => Object.fromEntries(inspections.map((record) => [record._id, record])),
    [inspections],
  )
  const history = useMemo(
    () =>
      [...inspections].sort(
        (a, b) => moment(b.examinationDate) - moment(a.examinationDate),
      ),
    [inspections],
  )
  // Latest record id per equipment that has been taken off the vehicle
  const retiredIds = new Set(
    inspections.filter((record) => record.retired).map((record) => record._id),
  )

  const applyResult = (data) =>
    setVehicle((prev) => ({
      ...prev,
      lolerInspections: data?.lolerInspections ?? prev.lolerInspections,
      lolerStatus: data?.lolerStatus ?? null,
    }))

  const setRetired = (record, retired) => {
    setBusy(record._id)
    API.put(`/fleet/${vehicle._id}/loler/${record._id}/retired`, { retired })
      .then((res) => {
        if (res.status === 200) {
          applyResult(res.data)
          setToast({
            message: retired
              ? `${equipmentName(record)} marked as removed from the vehicle`
              : `${equipmentName(record)} is being tracked again`,
          })
        } else {
          setToast({ message: res.data?.error || 'Unable to update.', severity: 'error' })
        }
      })
      .finally(() => setBusy(null))
  }

  const banner = status && status.state !== 'ok' && (
    <Alert
      severity={LOLER_STATE[status.state]?.tone === 'error' ? 'error' : 'warning'}
      action={
        status.state === 'missing' ? (
          <Button color="inherit" size="small" onClick={() => setForm({})}>
            Record
          </Button>
        ) : undefined
      }
    >
      {status.state === 'missing'
        ? 'This vehicle has a tail lift but no LOLER thorough examination is recorded.'
        : status.state === 'failed'
          ? 'Lifting equipment on this vehicle failed its last thorough examination - it must not be used until the defect is put right and it is re-examined.'
          : status.state === 'expired'
            ? 'A LOLER thorough examination is overdue. The equipment should not be used until it has been examined.'
            : `A LOLER thorough examination is due on ${formatDate(status.nextDue)}. Book it in now.`}
    </Alert>
  )

  return (
    <Stack spacing={1.5}>
      <SectionHeader
        title="LOLER"
        caption="Thorough examinations of lifting equipment - tail lifts, wheelchair lifts, ramp winches"
        chip={status && <StateChip state={status.state} />}
        action={
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setForm({})}
          >
            Record examination
          </Button>
        }
      />
      {banner}

      {!inspections.length && !status && (
        <Typography variant="body2" color="text.disabled">
          No lifting equipment recorded. If this vehicle has a tail lift, wheelchair lift or
          ramp winch, record its LOLER thorough examination here to track when the next one
          is due.
        </Typography>
      )}

      {status?.equipment?.length > 0 && (
        <Box
          display="grid"
          gap={1.5}
          gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr', xl: '1fr 1fr 1fr' }}
        >
          {status.equipment.map((item) => {
            const latest = byId[item.latestId] ?? {}
            return (
              <Card key={item.key} variant="outlined" sx={{ p: 1.75, borderRadius: 2.5 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start" mb={0.5}>
                  <Box flex={1} minWidth={0}>
                    <Typography fontWeight={800} noWrap>
                      {equipmentName(item)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      {latest.equipmentDescription || '—'}
                    </Typography>
                  </Box>
                  <StateChip state={item.state} />
                </Stack>
                <InfoList
                  rows={[
                    {
                      label: 'Next examination',
                      value: (
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          textAlign="right"
                          color={
                            item.state === 'ok'
                              ? 'text.primary'
                              : `${LOLER_STATE[item.state]?.tone}.main`
                          }
                        >
                          {dueText(item)}
                        </Typography>
                      ),
                    },
                    { label: 'Last examined', value: formatDate(item.examinationDate) },
                    {
                      label: 'Outcome',
                      value: (
                        <Chip
                          size="small"
                          label={item.outcome}
                          color={OUTCOME_COLOR[item.outcome]}
                          variant="outlined"
                          sx={{ borderRadius: 1 }}
                        />
                      ),
                    },
                    {
                      label: 'Safe working load',
                      value:
                        latest.safeWorkingLoadKg != null ? `${latest.safeWorkingLoadKg} kg` : null,
                    },
                    { label: 'Interval', value: latest.liftsPeople === false ? '12 months' : '6 months (lifts people)' },
                  ]}
                />
                {latest.defects && item.outcome !== 'Pass' && (
                  <Alert severity={item.outcome === 'Fail' ? 'error' : 'warning'} sx={{ mt: 1 }}>
                    {latest.defects}
                    {latest.remedialActionDue &&
                      ` — put right by ${formatDate(latest.remedialActionDue)}`}
                  </Alert>
                )}
                <Stack direction="row" spacing={1} mt={1.25} flexWrap="wrap" useFlexGap>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    onClick={() => setForm({ prefill: latest })}
                  >
                    Record re-examination
                  </Button>
                  <Button size="small" onClick={() => setForm({ existing: latest })}>
                    View / edit
                  </Button>
                  <Button
                    size="small"
                    color="inherit"
                    disabled={busy === latest._id}
                    onClick={() => setRetired(latest, true)}
                  >
                    Removed from vehicle
                  </Button>
                </Stack>
              </Card>
            )
          })}
        </Box>
      )}

      {history.length > 0 && (
        <>
          <Typography variant="subtitle2" fontWeight={700} pt={1}>
            Examination history
          </Typography>
          <CustomDataGrid
            getRowId={(row) => row._id}
            rows={history}
            maxHeight={400}
            columns={[
              {
                field: 'examinationDate',
                headerName: 'Examined',
                minWidth: 110,
                renderCell: ({ value }) => moment(value).format('DD/MM/YYYY'),
              },
              {
                field: 'equipmentType',
                headerName: 'Equipment',
                minWidth: 180,
                flex: 1,
                renderCell: ({ row }) => (
                  <Stack direction="row" spacing={0.75} alignItems="center" height="100%">
                    <span>{equipmentName(row)}</span>
                    {retiredIds.has(row._id) && (
                      <Chip
                        size="small"
                        label="Removed"
                        sx={{ borderRadius: 1, height: 20 }}
                        onClick={(e) => {
                          e.stopPropagation()
                          setRetired(row, false)
                        }}
                        title="Click to track this equipment again"
                      />
                    )}
                  </Stack>
                ),
              },
              {
                field: 'outcome',
                headerName: 'Outcome',
                minWidth: 170,
                renderCell: ({ value }) => (
                  <Chip
                    size="small"
                    label={value}
                    color={OUTCOME_COLOR[value]}
                    variant="outlined"
                    sx={{ borderRadius: 1 }}
                  />
                ),
              },
              {
                field: 'nextExaminationDue',
                headerName: 'Next due',
                minWidth: 110,
                renderCell: ({ value }) => (value ? moment(value).format('DD/MM/YYYY') : '—'),
              },
              {
                field: 'examinedBy',
                headerName: 'Examined by',
                minWidth: 180,
                flex: 1,
                renderCell: ({ row }) =>
                  [row.examinedBy, row.examiningCompany].filter(Boolean).join(', ') || '—',
              },
              { field: 'reportNumber', headerName: 'Report no.', minWidth: 120 },
              {
                field: 'documents',
                headerName: 'Docs',
                minWidth: 70,
                align: 'center',
                headerAlign: 'center',
                renderCell: ({ value }) =>
                  value?.length ? (
                    <Stack direction="row" spacing={0.25} alignItems="center" height="100%" justifyContent="center">
                      <AttachmentIcon fontSize="small" color="primary" />
                      <span>{value.length}</span>
                    </Stack>
                  ) : (
                    '—'
                  ),
              },
            ]}
            slots={{ toolbar: () => null, footer: () => null }}
            disableRowSelectionOnClick
            onRowClick={({ row }) => setForm({ existing: row })}
          />
        </>
      )}

      <BigModal open={!!form} onClose={() => setForm(null)} style={serviceModalStyle}>
        {form && (
          <AddLolerInspection
            key={form.existing?._id ?? form.prefill?._id ?? 'new'}
            existing={form.existing}
            prefill={form.prefill}
            onClose={() => setForm(null)}
            onSaved={applyResult}
          />
        )}
      </BigModal>
    </Stack>
  )
}

export default LolerSection
